import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Check, ArrowRight, ArrowLeft, Target, Zap, Users, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import useMetaTags from '../hooks/useMetaTags';
import useStructuredData from '../hooks/useStructuredData';
import { NavbarHTAG } from '../components/NavbarHTAG';
import { FooterHTAG } from '../components/FooterHTAG';

export const AccompagnementService = () => {
  // Meta tags SEO
  useMetaTags({
    title: 'Accompagnement Digital & IT en Ardennes | HTAG Facility',
    description: 'Service d\'accompagnement numérique 360° pour PME, associations et collectivités en Ardennes. Transformation digitale, conseil IT, formation. Basé à Château-Porcien.',
    keywords: 'accompagnement digital, conseil IT, transformation numérique, coaching digital, Ardennes, Château-Porcien, PME, associations',
    url: 'https://htagfacility.fr/services/accompagnement',
    image: 'https://htagfacility.fr/og-service-accompagnement.png',
    type: 'article'
  });

  // Structured Data - Service
  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Accompagnement Digital 360°',
    'provider': {
      '@type': 'LocalBusiness',
      'name': 'HTAG Facility',
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'Château-Porcien',
        'addressLocality': 'Château-Porcien',
        'addressRegion': 'Ardennes',
        'postalCode': '08320',
        'addressCountry': 'FR'
      }
    },
    'description': 'Accompagnement complet en transformation digitale et gestion IT pour PME et associations',
    'areaServed': ['FR-08', 'Ardennes'],
    'offers': {
      '@type': 'Offer',
      'description': 'Service d\'accompagnement numérique en visio ou sur site'
    }
  });
  return (
    <div className="bg-[#181C25] text-white lg:ml-16">
      <NavbarHTAG />

      {/* Bouton retour */}
      <div className="relative z-10">
        <div className="container mx-auto mb-8">
          <Link
            to="/?scroll=services"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors font-semibold hover:translate-x-1 duration-300"
          >
            <ArrowLeft size={18} />
            <span>Retour aux services</span>
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pb-24 bg-gradient-to-b from-[#212635] to-[#181C25]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center mb-20"
          >
            {/* Contenu */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-montserrat font-bold mb-6">
                Accompagnement <span className="text-blue-500">360°</span>
              </h1>
              <p className="text-xl text-gray-300 mb-4 leading-relaxed">
                Un accompagnement global et personnalisé pour transformer votre structure informatique et optimiser votre organisation digitale.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                Que vous soyez en phase de démarrage, de restructuration ou d'optimisation, nous vous accompagnons à chaque étape pour pérenniser votre croissance et renforcer votre compétitivité. <strong className="text-gray-300">Accompagnement en visio avec déplacement sur site si nécessaire.</strong>
              </p>
              <a
                href="/?scroll=contact"
                className="inline-flex items-center space-x-3 px-8 py-4 rounded-lg bg-blue-600 hover:bg-blue-700 font-montserrat font-semibold transition-all"
              >
                <span>Démarrer maintenant</span>
                <ArrowRight size={20} />
              </a>
            </div>

            {/* Visuel */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-96 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-500/30 overflow-hidden flex items-center justify-center"
            >
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=500&h=500&fit=crop" 
                alt="Accompagnement" 
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#181C25] to-transparent flex items-end justify-center pb-6">
                <p className="text-gray-300 font-montserrat text-lg font-semibold">Consultation Stratégique</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Détails du service - Les phases */}
      <section className="py-24">
        <div className="container mx-auto">
          <h2 className="text-4xl font-montserrat font-bold mb-16 text-white text-center">
            Les 3 phases de notre accompagnement
          </h2>
          
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-3 gap-8 mb-24"
          >
            {[
              {
                title: 'Phase 1: Audit & Diagnostic',
                icon: Target,
                description: 'Diagnostic complet de votre infrastructure IT, vos processus, vos défis et vos opportunités. Nous analysons en profondeur vos besoins actuels et vos objectifs de croissance.',
                details: ['Évaluation de l\'infrastructure', 'Analyse des processus', 'Identification des risques', 'Évaluation de la maturité digitale']
              },
              {
                title: 'Phase 2: Stratégie & Planning',
                icon: Zap,
                description: 'Construction d\'un plan d\'action détaillé, réaliste et adaptable. Définition des budgets, des délais et des indicateurs de performance mesurables.',
                details: ['Feuille de route détaillée', 'Budget prévisionnel', 'Timeline de mise en place', 'KPIs et métriques']
              },
              {
                title: 'Phase 3: Mise en place & Support',
                icon: Users,
                description: 'Implémentation progressive des solutions, formation des équipes et support continu. Assurance d\'une transition sans rupture de vos services.',
                details: ['Déploiement progressif', 'Formation des équipes', 'Support technique 24/7', 'Optimisations continues']
              },
            ].map((phase, idx) => {
              const Icon = phase.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="spotlight-card p-8 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 hover:border-blue-500 transition-all"
                >
                  <div className="flex items-center mb-4">
                    <div className="w-14 h-14 rounded-lg bg-blue-600 flex items-center justify-center mr-4">
                      <Icon size={28} />
                    </div>
                    <span className="text-3xl font-bold text-blue-400">{idx + 1}</span>
                  </div>
                  <h3 className="font-montserrat font-bold text-xl text-white mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-gray-300 mb-4">{phase.description}</p>
                  <ul className="space-y-2">
                    {phase.details.map((detail, didx) => (
                      <li key={didx} className="flex items-center space-x-2 text-gray-400 text-sm">
                        <Check size={14} className="text-blue-400 flex-shrink-0" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Section avec image - Avantages */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-12 items-center mb-24"
          >
            <div className="relative h-96 rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1552664730-f5b165b80c6d?w=500&h=500&fit=crop" 
                alt="Avantages" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#181C25] to-transparent"></div>
            </div>
            
            <div>
              <h2 className="text-4xl font-montserrat font-bold mb-8 text-white">
                Pourquoi choisir notre accompagnement ?
              </h2>
              <div className="space-y-4">
                {[
                  { title: 'Expertise métier', desc: 'Nous connaissons les défis spécifiques des TPE/PME en transformation digitale' },
                  { title: 'Approche personnalisée', desc: 'Chaque solution est adaptée à votre contexte, votre budget et vos objectifs' },
                  { title: 'Réduction des risques', desc: 'Un accompagnement progressif minimise les disruptions et les erreurs coûteuses' },
                  { title: 'Autonomie progressive', desc: 'Nous formons vos équipes pour les rendre autonomes et confiantes' },
                  { title: 'ROI mesurable', desc: 'Suivi régulier des indicateurs et ajustements pour maximiser votre retour sur investissement' },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="p-4 rounded-lg bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20"
                  >
                    <h3 className="font-montserrat font-bold text-white mb-1">{item.title}</h3>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Section des résultats */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mb-24"
          >
            <h2 className="text-4xl font-montserrat font-bold mb-12 text-white text-center">
              Les résultats de nos accompagnements
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { stat: '-35%', label: 'Coûts IT réduits en moyenne' },
                { stat: '+65%', label: 'Gains de productivité équipe' },
                { stat: '99.5%', label: 'Disponibilité des services' },
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-8 text-center rounded-xl bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-blue-500/30"
                >
                  <div className="text-4xl font-bold text-blue-400 mb-2">{item.stat}</div>
                  <p className="text-gray-300 font-semibold">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mb-24 p-12 rounded-2xl bg-gradient-to-r from-green-600/20 to-emerald-600/20 border border-green-500/30"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-green-400" size={28} />
                  <h3 className="text-2xl font-montserrat font-bold text-white">
                    Service en Ardennes - Château-Porcien
                  </h3>
                </div>
                <p className="text-gray-300 mb-4">
                  HTAG Facility est basé à <strong>Château-Porcien</strong> en Ardennes (08). Nous intervenons sur l'ensemble de la région pour accompagner PME, associations et collectivités dans leur transformation numérique. 
                </p>
                <p className="text-gray-400 text-sm">
                  <strong>Modes d'intervention :</strong> Accompagnement en visio ou déplacement sur site selon vos préférences et besoins.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-center p-6 rounded-xl bg-green-600/10 border border-green-500/30">
                  <p className="text-green-400 font-montserrat font-bold text-sm mb-2">ZONE INTERVENTION PRINCIPALE</p>
                  <p className="text-white font-bold text-lg">Ardennes</p>
                  <p className="text-gray-400 text-sm">Région Grand Est</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Final */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="text-center p-12 rounded-2xl bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30"
          >
            <h3 className="text-3xl font-montserrat font-bold mb-4 text-white">
              Prêt à transformer votre structure ?
            </h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Contactez Eric pour une première consultation gratuite de 30 minutes. Découvrez comment optimiser votre infrastructure IT et accélérer votre croissance.
            </p>
            <a
              href="/?scroll=contact"
              className="inline-flex items-center space-x-3 px-10 py-4 rounded-lg bg-blue-600 hover:bg-blue-700 font-montserrat font-semibold transition-all"
            >
              <span>Obtenir un devis gratuit</span>
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>

      <FooterHTAG />
    </div>
  );
};

export default AccompagnementService;
